<template>
  <div class="container">

    <!-- Search and Add Button -->
    <div class="flex-container justify-content-ceter m-2">
      <input type="text" v-model="searchString" placeholder="Enter Search String">
      <button type="button" class="btn btn-primary btn-sm m-2" @click="scanExpiryDates">ScanExpiryDates</button>
      <button type="button" class="btn btn-primary btn-sm m-2" @click="visaExpiryDatesDisplayNew(0, true)">DisplayExpiryDates</button>
    </div>

    <!-- Auth Message -->
    <div>
      <span v-show="!isAuthorized" class="text-danger h2">Please make sure you are logged in.</span>
    </div>

    <!-- VISA EXPIRY LIST -->
    <ExpiryTableSection
      :data="visaExpiryList"
      :total="visaExpiryCount"
      title="VISA EXPIRY LIST"
      :current-page="currentVisaExpiryPageNo"
      :start-page="visaStartPageNumber"
      :end-page="visaEndPageNumber"
      :is-range-greater="visaIsRangeGreater"
      :is-range-less="visaIsRangeLess"
      :labels-per-page="LabelsPerPage"
      :expired-count="visaExpiredCount"
      :immediately-expiring-count="visaImmediatelyExpiringCount"
      :soon-expiring-count="visaSoonExpiringCount"
      :valid-count="visaValidCount"
      @prev-page="visaExpiryDatesDisplayNew(visaStartPageNumber - LabelsPerPage)"
      @next-page="visaExpiryDatesDisplayNew(visaEndPageNumber)"
      @page-change="visaExpiryDatesDisplayNew($event)"
      @filter-change="(filters) => handleFilterChange('Visa', filters)"
    />

    <!-- PASSPORT EXPIRY LIST -->
    <ExpiryTableSection
      :data="passportExpiryList"
      :total="passportExpiryCount"
      title="PASSPORT EXPIRY LIST"
      :current-page="currentPassportExpiryPageNo"
      :start-page="passportStartPageNumber"
      :end-page="passportEndPageNumber"
      :is-range-greater="passportIsRangeGreater"
      :is-range-less="passportIsRangeLess"
      :labels-per-page="LabelsPerPage"
      :expired-count="passportExpiredCount"
      :immediately-expiring-count="passportImmediatelyExpiringCount"
      :soon-expiring-count="passportSoonExpiringCount"
      :valid-count="passportValidCount"
      @prev-page="passportExpiryDatesDisplayNew(passportStartPageNumber - LabelsPerPage)"
      @next-page="passportExpiryDatesDisplayNew(passportEndPageNumber)"
      @page-change="passportExpiryDatesDisplayNew($event)"
      @filter-change="(filters) => handleFilterChange('Passport', filters)"
    />

    <!-- SIA BADGE EXPIRY LIST -->
    <ExpiryTableSection
      :data="siaBadgeExpiryList"
      :total="siaBadgeExpiryCount"
      title="SIA BADGE EXPIRY LIST"
      :current-page="currentSiaBadgeExpiryPageNo"
      :start-page="siaStartPageNumber"
      :end-page="siaEndPageNumber"
      :is-range-greater="siaIsRangeGreater"
      :is-range-less="siaIsRangeLess"
      :labels-per-page="LabelsPerPage"
      :expired-count="siaExpiredCount"
      :immediately-expiring-count="siaImmediatelyExpiringCount"
      :soon-expiring-count="siaSoonExpiringCount"
      :valid-count="siaValidCount"
      @prev-page="siaBadgeExpiryDatesDisplayNew(siaStartPageNumber - LabelsPerPage)"
      @next-page="siaBadgeExpiryDatesDisplayNew(siaEndPageNumber)"
      @page-change="siaBadgeExpiryDatesDisplayNew($event)"
      @filter-change="(filters) => handleFilterChange('SIA_Badge', filters)"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import IExpiryDatesScan from './components/IExpiryDatesScan';
import ExpiryTableSection from '@/components/ExpiryTableSection.vue';
import { formatDate } from '@/Filters/dateFilter';

// Props
interface Props {
  securityOfficerId?: string;
  baseUrl?: string;
}

const props = defineProps<Props>();

// Constants
const LabelsPerPage = 10;
const smsapi_endpoint_base_url = '';

// Reactive state
const visaExpiryList = ref<IExpiryDatesScan[]>([]);
const passportExpiryList = ref<IExpiryDatesScan[]>([]);
const siaBadgeExpiryList = ref<IExpiryDatesScan[]>([]);
const searchString = ref<string>('');
const currentVisaExpiryPageNo = ref<number>(0);
const currentPassportExpiryPageNo = ref<number>(0);
const currentSiaBadgeExpiryPageNo = ref<number>(0);
const recordsPerPage = ref<number>(10);
const isAuthorized = ref<boolean>(false);

// Visa pagination state
const visaStartPageNumber = ref<number>(1);
const visaEndPageNumber = ref<number>(0);
const visaIsRangeGreater = ref<boolean>(false);
const visaIsRangeLess = ref<boolean>(false);
const visaExpiryCount = ref<number>(0);

// Passport pagination state
const passportStartPageNumber = ref<number>(1);
const passportEndPageNumber = ref<number>(0);
const passportIsRangeGreater = ref<boolean>(false);
const passportIsRangeLess = ref<boolean>(false);
const passportExpiryCount = ref<number>(0);

// SIA Badge pagination state
const siaStartPageNumber = ref<number>(1);
const siaEndPageNumber = ref<number>(0);
const siaIsRangeGreater = ref<boolean>(false);
const siaIsRangeLess = ref<boolean>(false);
const siaBadgeExpiryCount = ref<number>(0);

// Summary counts
const visaExpiredCount = ref<number>(0);
const visaImmediatelyExpiringCount = ref<number>(0);
const visaSoonExpiringCount = ref<number>(0);
const visaValidCount = ref<number>(0);

const passportExpiredCount = ref<number>(0);
const passportImmediatelyExpiringCount = ref<number>(0);
const passportSoonExpiringCount = ref<number>(0);
const passportValidCount = ref<number>(0);

const siaExpiredCount = ref<number>(0);
const siaImmediatelyExpiringCount = ref<number>(0);
const siaSoonExpiringCount = ref<number>(0);
const siaValidCount = ref<number>(0);

// Filter state
const currentFilters = ref<{ [key: string]: any }>({});

// Handle filter change from child component
const handleFilterChange = (documentType: string, filters: any[]) => {
  // Calculate MinDaysUntilExpiry and MaxDaysUntilExpiry from checked filters
  let minDays: number | null = null;
  let maxDays: number | null = null;

  filters.forEach((item: any) => {
    if (!item.checked) return;

    let itemMin: number | null = null;
    let itemMax: number | null = null;

    switch (item.key) {
      case 'expired':
        itemMin = undefined;//-999;
        itemMax = 0;
        break;
      case 'immediately':
        itemMin = 1;
        itemMax = 7;
        break;
      case 'soon':
        itemMin = 8;
        itemMax = 29;
        break;
      case 'valid':
        itemMin = 30;
        itemMax = undefined;//999;
        break;
    }

    if (itemMin !== null) {
      if (minDays === null || itemMin < minDays) minDays = itemMin;
    }
    if (itemMax !== null) {
      if (maxDays === null || itemMax > maxDays) maxDays = itemMax;
    }
  });

   const anyChecked = filters.some((item: any) => item.checked);
  if (!anyChecked) {
    // Use a range that returns no records
    minDays = 999;
    maxDays = -999;
  } 

  // Store current filters
  currentFilters.value[documentType] = { minDays, maxDays };

  //console.log("minDays, maxDays = ", minDays, maxDays);

  // Reset to page 0 and fetch with new filters - USE THE WORKING FUNCTIONS
  switch (documentType) {
    case 'Visa':
      currentVisaExpiryPageNo.value = 0;
      visaExpiryDatesDisplayNew(0, false);  // ← CHANGE THIS
      break;
    case 'Passport':
      currentPassportExpiryPageNo.value = 0;
      passportExpiryDatesDisplayNew(0);     // ← CHANGE THIS
      break;
    case 'SIA_Badge':
      currentSiaBadgeExpiryPageNo.value = 0;
      siaBadgeExpiryDatesDisplayNew(0);     // ← CHANGE THIS
      break;
  }
};

// Generic function to fetch expiry dates with filters
const fetchExpiryDates = (documentType: string, pageNumber: number, minDays?: number | null, maxDays?: number | null): Promise<any> => {
  const body: any = {
    'PageNumber': pageNumber,
    'RecordsPerPage': recordsPerPage.value,
    'SearchString': searchString.value,
  };

  // Add days filters if provided
  if (minDays !== undefined && minDays !== null) {
    body['MinDaysUntilExpiry'] = minDays;
  }
  if (maxDays !== undefined && maxDays !== null) {
    body['MaxDaysUntilExpiry'] = maxDays;
  }

  const options: RequestInit = {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body),
  };

  const url = (props.baseUrl ?? smsapi_endpoint_base_url) + 
    `Securityofficers/GetExpiryDates?documentType=${documentType}`;

  return fetch(url, options)
    .then((response: Response) => {
      if (response.status === 401) {
        isAuthorized.value = false;
        return null;
      }
      isAuthorized.value = true;
      return response.json();
    });
};

const visaExpiryDatesDisplayNew = (pageNumber: number, reset: boolean = false): void => {
  currentVisaExpiryPageNo.value = pageNumber;

  const filters = currentFilters.value['Visa'] || {};
  fetchExpiryDates('Visa', pageNumber, filters.minDays, filters.maxDays)
    .then(results => {
      if (!results) return;
      
      visaExpiryList.value = results.expiriesViewList;
      visaExpiryCount.value = results.totalRecordCount;
      
      // Update summary counts
      visaExpiredCount.value = results.expiredCount || 0;
      visaImmediatelyExpiringCount.value = results.immediatelyExpiringCount || 0;
      visaSoonExpiringCount.value = results.soonExpiringCount || 0;
      visaValidCount.value = results.validCount || 0;

      const visaResultPages = Math.ceil(visaExpiryCount.value / recordsPerPage.value);
      displayVisaResultPageList(currentVisaExpiryPageNo.value, visaResultPages);

      if (pageNumber === 0 && reset === true) {
        currentPassportExpiryPageNo.value = 0;
        passportExpiryDatesDisplayNew(0);
        currentSiaBadgeExpiryPageNo.value = 0;
        siaBadgeExpiryDatesDisplayNew(0);
      }
    })
    .catch((error: Error) => {
      console.error("Error in visaExpiryDatesDisplayNew:", error);
    });
};

const passportExpiryDatesDisplayNew = (pageNumber: number): void => {
  currentPassportExpiryPageNo.value = pageNumber;

  const filters = currentFilters.value['Passport'] || {};
  fetchExpiryDates('Passport', pageNumber, filters.minDays, filters.maxDays)
    .then(results => {
      if (!results) return;
      
      passportExpiryList.value = results.expiriesViewList;
      passportExpiryCount.value = results.totalRecordCount;
      
      passportExpiredCount.value = results.expiredCount || 0;
      passportImmediatelyExpiringCount.value = results.immediatelyExpiringCount || 0;
      passportSoonExpiringCount.value = results.soonExpiringCount || 0;
      passportValidCount.value = results.validCount || 0;

      const passportResultPages = Math.ceil(passportExpiryCount.value / recordsPerPage.value);
      displayPassportResultPageList(currentPassportExpiryPageNo.value, passportResultPages);
    })
    .catch((error: Error) => {
      console.error("Error in passportExpiryDatesDisplayNew:", error);
    });
};

const siaBadgeExpiryDatesDisplayNew = (pageNumber: number): void => {
  currentSiaBadgeExpiryPageNo.value = pageNumber;

  const filters = currentFilters.value['SIA_Badge'] || {};
  fetchExpiryDates('SIA_Badge', pageNumber, filters.minDays, filters.maxDays)
    .then(results => {
      if (!results) return;
      
      siaBadgeExpiryList.value = results.expiriesViewList;
      siaBadgeExpiryCount.value = results.totalRecordCount;
      
      siaExpiredCount.value = results.expiredCount || 0;
      siaImmediatelyExpiringCount.value = results.immediatelyExpiringCount || 0;
      siaSoonExpiringCount.value = results.soonExpiringCount || 0;
      siaValidCount.value = results.validCount || 0;

      const siaResultPages = Math.ceil(siaBadgeExpiryCount.value / recordsPerPage.value);
      displaySiaResultPageList(currentSiaBadgeExpiryPageNo.value, siaResultPages);
    })
    .catch((error: Error) => {
      console.error("Error in siaBadgeExpiryDatesDisplayNew:", error);
    });
};

const displayVisaResultPageList = (currentPageNo, totalResultPages: number = 4): void => {
  const sectionNo = Math.floor(currentPageNo / LabelsPerPage);
  visaStartPageNumber.value = sectionNo * LabelsPerPage;
  visaEndPageNumber.value = (sectionNo + 1) * LabelsPerPage;

  if (visaEndPageNumber.value >= totalResultPages) {
    visaIsRangeGreater.value = false;
    visaEndPageNumber.value = totalResultPages;
  } else {
    visaIsRangeGreater.value = true;
  }

  visaIsRangeLess.value = visaStartPageNumber.value >= LabelsPerPage;
};

const displayPassportResultPageList = (currentPageNo, totalResultPages: number = 4): void => {
  const sectionNo = Math.floor(currentPageNo / LabelsPerPage);
  passportStartPageNumber.value = sectionNo * LabelsPerPage;
  passportEndPageNumber.value = (sectionNo + 1) * LabelsPerPage;

  if (passportEndPageNumber.value >= totalResultPages) {
    passportIsRangeGreater.value = false;
    passportEndPageNumber.value = totalResultPages;
  } else {
    passportIsRangeGreater.value = true;
  }

  passportIsRangeLess.value = passportStartPageNumber.value >= LabelsPerPage;
};

const displaySiaResultPageList = (currentPageNo, totalResultPages: number = 4): void => {
  const sectionNo = Math.floor(currentPageNo / LabelsPerPage);
  siaStartPageNumber.value = sectionNo * LabelsPerPage;
  siaEndPageNumber.value = (sectionNo + 1) * LabelsPerPage;

  if (siaEndPageNumber.value >= totalResultPages) {
    siaIsRangeGreater.value = false;
    siaEndPageNumber.value = totalResultPages;
  } else {
    siaIsRangeGreater.value = true;
  }

  siaIsRangeLess.value = siaStartPageNumber.value >= LabelsPerPage;
};

const scanExpiryDates = (): void => {
  const options: RequestInit = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.token}`,
      'Content-Type': 'application/json'
    },
  };
  
  const url = (props.baseUrl ?? smsapi_endpoint_base_url) + "Securityofficers/ScanExiryDates";
  fetch(url, options)
    .then((response: Response) => {
      if (response.status === 401) {
        isAuthorized.value = false;
        return null;
      }
      isAuthorized.value = true;
      return response.json();
    })
    .catch((error: Error) => {
      console.error("Error in scanExpiryDates:", error);
    });
};

const getAuthorization = (): void => {
  const token = localStorage.getItem("token");
  isAuthorized.value = token !== null && token !== "";
};

// Lifecycle hooks
onMounted(() => {
  getAuthorization();
});

watch(
  () => props.securityOfficerId,
  (newVal: string | undefined) => {
    console.log("selectedOfficerIdForModal changed to:", newVal);
  }
);

defineExpose({
  scanExpiryDates,
});
</script>

<style scoped>
.container {
  padding: 10px;
}

.flex-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}

.btn-selected {
  background: blue;
  color: white;
}
</style>